import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions as MuiDialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import axios from 'axios';
import ThemePurchaseCTA from './ThemePurchaseCTA';
import {
  FAILURE_STATUSES,
  IN_PROGRESS_STATUSES,
  formatProjectDate,
  getProjectBucket,
  getProjectModeLabel,
  getProjectPrimaryAction,
  getProjectStatusLabel,
  getProjectTimestamp,
  isProjectPaymentRequired,
  isProjectReadyToDownload,
} from '../utils/projectState';

const ProjectCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'accentcolor',
})(({ theme, accentcolor }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(2),
  borderTop: `4px solid ${accentcolor || theme.palette.divider}`,
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const SummaryCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  height: '100%',
}));

const SectionShell = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
}));

const ProjectsList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [currentMenuProject, setCurrentMenuProject] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMode, setFilterMode] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingProjects, setDownloadingProjects] = useState(new Set());

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/projects');
      setProjects(response.data.projects || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setError('Failed to load projects. Please try again later.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleDownload = async (project) => {
    try {
      setDownloadingProjects((prev) => new Set([...prev, project.id]));

      const authToken = window.localStorage.getItem('token');
      const response = await axios.get(`/api/download/${project.uuid}`, {
        responseType: 'blob',
        headers: {
          Authorization: ['Bearer', authToken].join(' '),
        },
      });

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.name || 'theme'}-${project.id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Download error:', downloadError);

      if (downloadError.response && downloadError.response.data instanceof Blob) {
        const errorText = await downloadError.response.data.text();
        setError(`Download failed: ${errorText}`);
      } else {
        setError(`Download failed: ${downloadError.response?.data?.error || downloadError.message}`);
      }
    } finally {
      setDownloadingProjects((prev) => {
        const next = new Set(prev);
        next.delete(project.id);
        return next;
      });
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchProjects();
  };

  const handleMenuOpen = (event, project) => {
    setMenuAnchorEl(event.currentTarget);
    setCurrentMenuProject(project);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setCurrentMenuProject(null);
  };

  const handleDeleteDialogOpen = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  const handleDeleteProject = async () => {
    try {
      if (!projectToDelete) return;

      await axios.delete(`/api/projects/${projectToDelete.id}`);
      setProjects((currentProjects) => currentProjects.filter((project) => project.id !== projectToDelete.id));
      handleDeleteDialogClose();
    } catch (err) {
      console.error('Failed to delete project:', err);
      setError(`Failed to delete project: ${err.response?.data?.message || err.message}`);
    }
  };

  const filteredProjects = useMemo(() => (
    [...projects]
      .filter((project) => {
        if (filterStatus !== 'all' && project.status !== filterStatus) {
          return false;
        }

        if (filterMode !== 'all' && project.mode !== filterMode) {
          return false;
        }

        if (searchTerm) {
          const haystack = `${project.name || ''} ${project.themeName || ''}`.toLowerCase();
          return haystack.includes(searchTerm.toLowerCase());
        }

        return true;
      })
      .sort((a, b) => {
        const dateA = new Date(getProjectTimestamp(a) || 0);
        const dateB = new Date(getProjectTimestamp(b) || 0);
        return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
      })
  ), [projects, filterStatus, filterMode, searchTerm, sortOrder]);

  const groupedProjects = useMemo(() => {
    const groups = {
      inProgress: [],
      actionNeeded: [],
      completed: [],
      other: [],
    };

    filteredProjects.forEach((project) => {
      groups[getProjectBucket(project)].push(project);
    });

    return groups;
  }, [filteredProjects]);

  const summary = useMemo(() => ({
    inProgress: projects.filter((project) => IN_PROGRESS_STATUSES.includes(project.status)).length,
    actionNeeded: projects.filter((project) => FAILURE_STATUSES.includes(project.status) || isProjectPaymentRequired(project)).length,
    completed: projects.filter(isProjectReadyToDownload).length,
    total: projects.length,
  }), [projects]);

  const sections = [
    {
      key: 'inProgress',
      title: 'In Progress',
      description: 'Themes that are actively generating or still packaging.',
      color: 'info.main',
      empty: 'No active generations right now.',
      icon: <AccessTimeIcon color="info" />,
    },
    {
      key: 'actionNeeded',
      title: 'Action Needed',
      description: 'Completed themes waiting for payment, or projects that need attention.',
      color: 'warning.main',
      empty: 'Nothing needs your attention right now.',
      icon: <WarningAmberIcon color="warning" />,
    },
    {
      key: 'completed',
      title: 'Completed',
      description: 'Finished projects that are ready for download.',
      color: 'success.main',
      empty: 'Completed downloads will show up here.',
      icon: <CheckCircleIcon color="success" />,
    },
    {
      key: 'other',
      title: 'Other Projects',
      description: 'Anything outside the main workflow buckets.',
      color: 'grey.500',
      empty: 'No other projects to show.',
      icon: <FolderOpenIcon color="action" />,
    },
  ];

  const getAccentColor = (project) => {
    const bucket = getProjectBucket(project);
    const colorMap = {
      inProgress: '#0288d1',
      actionNeeded: '#ed6c02',
      completed: '#2e7d32',
      other: '#9e9e9e',
    };

    return colorMap[bucket];
  };

  const getNextActionSummary = (project) => {
    const action = getProjectPrimaryAction(project);

    if (action.key === 'pay') {
      return 'Complete payment to unlock your download.';
    }

    if (action.key === 'download') {
      return 'Download your finished theme.';
    }

    if (action.key === 'resume') {
      return project.message || 'Check progress and wait for generation to finish.';
    }

    if (project.status === 'failed' || project.status === 'error') {
      return 'Open the project to retry or review the failure.';
    }

    return 'Open this project for full details.';
  };

  const renderProjectActions = (project) => {
    const primaryAction = getProjectPrimaryAction(project);

    return (
      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2, pt: 0, alignItems: 'flex-start' }}>
        <Button
          component={RouterLink}
          to={`/projects/${project.id}`}
          size="small"
          color="primary"
          startIcon={<VisibilityIcon />}
        >
          {primaryAction.key === 'resume' ? 'Resume Project' : 'View Details'}
        </Button>

        {primaryAction.key === 'download' && (
          <Button
            onClick={() => handleDownload(project)}
            size="small"
            variant="contained"
            color="success"
            startIcon={downloadingProjects.has(project.id) ? <CircularProgress size={16} /> : <DownloadIcon />}
            disabled={downloadingProjects.has(project.id)}
          >
            {downloadingProjects.has(project.id) ? 'Downloading...' : 'Download'}
          </Button>
        )}

        {primaryAction.key === 'pay' && (
          <Box sx={{ minWidth: 170 }}>
            <ThemePurchaseCTA
              projectId={project.id}
              projectMode={project.mode}
              paymentStatus={project.payment_status || 'unpaid'}
              projectStatus={project.status}
              context="projects"
              onPaymentSuccess={fetchProjects}
            />
          </Box>
        )}
      </CardActions>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Projects
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your full theme generation history, grouped by what needs attention next.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button component={RouterLink} to="/quick-themes" variant="contained" startIcon={<BoltIcon />}>
            Quick Theme
          </Button>
          <Button component={RouterLink} to="/ai-custom" variant="outlined" startIcon={<AutoAwesomeIcon />}>
            Custom Theme
          </Button>
          <Button component={RouterLink} to="/profile/dashboard" variant="outlined" startIcon={<BusinessIcon />}>
            Business Profiles
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard elevation={0}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              In Progress
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="info.main">
              {summary.inProgress}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active generations still running.
            </Typography>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard elevation={0}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Action Needed
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="warning.main">
              {summary.actionNeeded}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Payments or failed runs to review.
            </Typography>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard elevation={0}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Completed
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {summary.completed}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ready to download right now.
            </Typography>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard elevation={0}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Total Projects
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {summary.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Full project library across all theme work.
            </Typography>
          </SummaryCard>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>

          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
                startAdornment={(
                  <InputAdornment position="start">
                    <FilterListIcon fontSize="small" />
                  </InputAdornment>
                )}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="generating">Generating</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="packaging">Packaging</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="mode-filter-label">Mode</InputLabel>
              <Select
                labelId="mode-filter-label"
                value={filterMode}
                onChange={(e) => setFilterMode(e.target.value)}
                label="Mode"
                startAdornment={(
                  <InputAdornment position="start">
                    <FilterListIcon fontSize="small" />
                  </InputAdornment>
                )}
              >
                <MenuItem value="all">All Modes</MenuItem>
                <MenuItem value="quick">Quick Theme</MenuItem>
                <MenuItem value="custom">Custom Theme</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="sort-order-label">Sort By</InputLabel>
              <Select
                labelId="sort-order-label"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                label="Sort By"
                startAdornment={(
                  <InputAdornment position="start">
                    <SortIcon fontSize="small" />
                  </InputAdornment>
                )}
              >
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : filteredProjects.length > 0 ? (
        <Box sx={{ display: 'grid', gap: 3 }}>
          {sections.map((section) => (
            groupedProjects[section.key].length > 0 && (
              <SectionShell elevation={0} key={section.key}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {section.icon}
                      <Typography variant="h5" fontWeight="bold">
                        {section.title}
                      </Typography>
                      <Chip label={groupedProjects[section.key].length} size="small" />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {section.description}
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  {groupedProjects[section.key].map((project) => (
                    <Grid item xs={12} sm={6} lg={4} key={project.id}>
                      <ProjectCard accentcolor={getAccentColor(project)}>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="h6" component="h3" title={project.name} noWrap fontWeight={700}>
                                {project.name || `Project #${project.id}`}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {project.themeName || getProjectModeLabel(project.mode)}
                              </Typography>
                            </Box>

                            <IconButton size="small" onClick={(event) => handleMenuOpen(event, project)} aria-label="project options">
                              <MoreVertIcon />
                            </IconButton>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            <Chip label={getProjectStatusLabel(project.status)} size="small" />
                            <Chip label={getProjectModeLabel(project.mode)} size="small" variant="outlined" />
                            {isProjectPaymentRequired(project) && (
                              <Chip label="Payment Required" size="small" color="warning" />
                            )}
                            {isProjectReadyToDownload(project) && (
                              <Chip label="Ready to Download" size="small" color="success" />
                            )}
                          </Box>

                          <Divider sx={{ mb: 2 }} />

                          <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Created
                              </Typography>
                              <Typography variant="body2">
                                {formatProjectDate(getProjectTimestamp(project), { includeTime: true })}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Next Action
                              </Typography>
                              <Typography variant="body2">
                                {getProjectPrimaryAction(project).label}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">
                                Status Summary
                              </Typography>
                              <Typography variant="body2">
                                {getNextActionSummary(project)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>

                        {renderProjectActions(project)}
                      </ProjectCard>
                    </Grid>
                  ))}
                </Grid>
              </SectionShell>
            )
          ))}
        </Box>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" gutterBottom>
            No projects found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Try adjusting your filters or start a new theme.
          </Typography>

          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button component={RouterLink} to="/quick-themes" variant="contained">
              Create Quick Theme
            </Button>
            <Button component={RouterLink} to="/ai-custom" variant="outlined">
              Create Custom Theme
            </Button>
          </Box>
        </Paper>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem component={RouterLink} to={`/projects/${currentMenuProject?.id}`}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>

        {currentMenuProject && isProjectReadyToDownload(currentMenuProject) && (
          <MenuItem
            onClick={() => {
              handleDownload(currentMenuProject);
              handleMenuClose();
            }}
            disabled={downloadingProjects.has(currentMenuProject?.id)}
          >
            <ListItemIcon>
              {downloadingProjects.has(currentMenuProject?.id)
                ? <CircularProgress size={16} />
                : <DownloadIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>
              {downloadingProjects.has(currentMenuProject?.id) ? 'Downloading...' : 'Download Theme'}
            </ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={() => handleDeleteDialogOpen(currentMenuProject)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Project</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the project "{projectToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <MuiDialogActions>
          <Button onClick={handleDeleteDialogClose}>
            Cancel
          </Button>
          <Button onClick={handleDeleteProject} color="error" variant="contained">
            Delete
          </Button>
        </MuiDialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectsList;
